import * as React from 'react';
import styles from './index.less';
import { Input, InputRef } from 'antd';
import { FocusEventHandler, KeyboardEventHandler } from 'react';
import { FUtil } from '@freelog/tools-lib';
import * as AHooks from 'ahooks';

export interface FInput_PinyinSafeTextCounter_Props {
  value: string;
  placeholder?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  className?: string;
  size?: 'small' | 'middle';
  style?: React.CSSProperties;
  lengthLimit?: number;
  hasError?: boolean;

  prefix?: React.ReactNode;
  suffix?: React.ReactNode;

  onPressEnter?: KeyboardEventHandler<HTMLInputElement>;
  onKeyUp?: KeyboardEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;

  onChangeValue?(value: string): void;
}

function FInput_PinyinSafeTextCounter(
  {
    value,
    placeholder = '',
    autoFocus = false,
    disabled = false,
    className = '',
    size = 'middle',
    style = {},
    lengthLimit = 100,
    hasError = false,
    prefix,
    suffix,
    onPressEnter,
    onKeyUp,
    onBlur,
    onChangeValue,
  }: FInput_PinyinSafeTextCounter_Props,
  ref: React.Ref<InputRef> | undefined,
) {
  const [$flag, set$flag, get$flag] = FUtil.Hook.useGetState<boolean>(true);
  const [$value, set$value, get$value] = FUtil.Hook.useGetState<string>('');

  AHooks.useDebounceEffect(
    () => {
      if (value !== get$value()) {
        set$value(value);
      }
    },
    [value],
    {
      wait: 10,
    },
  );

  /** 处理文本长度限制，如果超出字数限制，截断新输入超出的文本 */
  function handleTextLimitChange(event: React.ChangeEvent<HTMLInputElement>) {
    event.preventDefault();
    event.stopPropagation();
    const v = event.target.value;
    const cursorPos = event.target.selectionStart ?? 0;
    if (lengthLimit <= 0 || v.length <= lengthLimit) {
      onChangeValue?.(v);
      set$value(v);
      return;
    }

    const beforeCursor = v.substring(0, cursorPos);
    const afterCursor = v.substring(cursorPos);

    const exceed = v.length - lengthLimit;
    const truncatedBeforeCursor = beforeCursor.substring(0, beforeCursor.length - exceed);
    const truncatedValue = truncatedBeforeCursor + afterCursor;
    onChangeValue?.(truncatedValue);
    set$value(truncatedValue);
    if (event.target.selectionStart !== truncatedBeforeCursor.length) {
      setTimeout(() => {
        (event.target as HTMLInputElement).setSelectionRange(
          truncatedBeforeCursor.length,
          truncatedBeforeCursor.length,
        );
      }, 1);
    }
  }

  return (
    <Input
      prefix={prefix}
      autoFocus={autoFocus}
      disabled={disabled}
      value={$value}
      ref={ref}
      placeholder={placeholder}
      className={[
        className,
        disabled
          ? styles.disabled_FInput_PinyinSafeTextCounter
          : styles.FInput_PinyinSafeTextCounter,
        hasError ? styles.InputError : '',
      ].join(' ')}
      style={{
        height: size === 'middle' ? 38 : 32,
        ...style,
      }}
      onPressEnter={onPressEnter}
      onKeyUp={onKeyUp}
      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
        if (get$flag()) {
          handleTextLimitChange(event);
        } else {
          set$value(event.target.value);
        }
      }}
      suffix={
        suffix ||
        (lengthLimit > 0 ? (
          <span
            className={[
              styles.FInputWordCount,
              lengthLimit - value.length < 0 ? styles.beyond : '',
            ].join(' ')}
          >
            {lengthLimit - value.length}
          </span>
        ) : (
          <span />
        ))
      }
      onBlur={onBlur}
      onCompositionStart={(event) => {
        set$flag(false);
      }}
      onCompositionEnd={(event: React.CompositionEvent<HTMLInputElement>) => {
        set$flag(true);
        handleTextLimitChange(event as any);
      }}
    />
  );
}

export default React.forwardRef(FInput_PinyinSafeTextCounter);
